import React from 'react';
import { useAppDispatch } from '../store/hooks';
import {
  Popover,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as EmptyCartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { updateQuantityAsync, removeFromCartAsync } from '../store/slices/cartSlice';
import { cartLineMax } from '../utils/stock';
import { ImageSourceItem, itemImageUrl } from '../utils/itemImage';
import { CartItem } from '../types';

interface MiniCartDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const MiniCartDropdown: React.FC<MiniCartDropdownProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, totalPrice } = useSelector((state: RootState) => state.cart);

  const handleQuantityChange = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const item = items.find((i: any) => i.id === itemId);
    // Plafon csak polckészletnél; utánrendelésnél nincs (Infinity).
    const max = item ? cartLineMax(item) : Infinity;
    if (Number.isFinite(max) && newQuantity > max) return;
    await dispatch(updateQuantityAsync({ itemId, quantity: newQuantity }));
  };

  const handleRemoveItem = async (itemId: number) => {
    await dispatch(removeFromCartAsync(itemId));
  };

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleViewCart = () => {
    onClose();
    navigate('/kosar');
  };

  // A tervezőben készült terv látványképe az első (lásd itemImageUrl).
  const getProductImage = (item: ImageSourceItem) => itemImageUrl(item) || '/placeholder-product.png';

  const getProductName = (item: CartItem) => {
    let name = item.product.name;
    if (item.product_variant) {
      // (Korábban nem létező color_name/size_name mezőket olvasott — sosem jelent meg.)
      const details: string[] = [];
      if (item.product_variant.color) details.push(item.product_variant.color);
      if (item.product_variant.size) details.push(item.product_variant.size);
      if (details.length > 0) {
        name += ` (${details.join(', ')})`;
      }
    }
    return name;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          mt: 1.5,
          width: { xs: '100vw', sm: 400 },
          maxWidth: 400,
          maxHeight: 500,
          borderRadius: 2,
          overflow: 'visible',
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Kosár ({items.length} termék)
        </Typography>

        {items.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <EmptyCartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="body2">A kosarad üres</Typography>
          </Box>
        ) : (
          <>
            {/* Cart Items — a lista görgethető; a magasságot úgy korlátozzuk, hogy
                a fejléc + összeg + gombok (footer) mindig beférjen a Paper 500px-ébe
                (különben a „Kosár megtekintése" gomb kilóg a panel alján). */}
            <Box
              sx={{
                maxHeight: 240,
                overflowY: 'auto',
                mb: 2,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {items.map((item) => (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 1.5,
                    p: 1.5,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    position: 'relative',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  {/* Product Image */}
                  <Box
                    component="img"
                    src={getProductImage(item)}
                    alt={item.product.name}
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />

                  {/* Product Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getProductName(item)}
                    </Typography>

                    {/* Quantity Controls */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'white',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          sx={{ p: 0.5 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{
                            px: 1,
                            minWidth: 30,
                            textAlign: 'center',
                            fontWeight: 600,
                          }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={(() => {
                            const max = cartLineMax(item);
                            return Number.isFinite(max) && item.quantity >= max;
                          })()}
                          sx={{ p: 0.5 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ fontWeight: 600, ml: 'auto' }}
                      >
                        {Math.round(item.total_price).toLocaleString('hu-HU')} Ft
                      </Typography>
                    </Box>
                  </Box>

                  {/* Delete Button */}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveItem(item.id)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Total */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2,
                px: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Összesen:
              </Typography>
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: 700 }}
              >
                {Math.round(totalPrice).toLocaleString('hu-HU')} Ft
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleCheckout}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Folytatás a fizetéshez
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="medium"
                onClick={handleViewCart}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Kosár megtekintése
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
};

export default MiniCartDropdown;
